import type { AxiosError } from "axios";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { saveBlob } from "src/utils/download";
import { useSnackbar } from "src/utils/useSnackbar";

export type ListExportFormat = "xlsx" | "csv";

const LIST_EXPORT_MIME_TYPES: Record<ListExportFormat, string> = {
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    csv: "text/csv",
};

export type ListExportParamsInput = {
    filters: AxiosParams;
    sort: AxiosParams;
    search: string;
    campaignId?: number;
    organizationId?: number;
    extra?: AxiosParams;
};

export const toListExportParams = (input: ListExportParamsInput): AxiosParams => {
    const { filters, sort, search, campaignId, organizationId, extra = {} } = input;

    return {
        ...filters,
        ...sort,
        ...extra,
        q: search === "" ? undefined : search,
        campaign: campaignId,
        organization_id: organizationId,
    };
};

export const listExportFilename = (fileNameStem: string, format: ListExportFormat) =>
    `${fileNameStem}-export.${format}`;

type UseListExportOptions = {
    path: string;
    params: AxiosParams;
    fileNameStem: string;
    successMessage?: string;
};

export const useListExport = (options: UseListExportOptions) => {
    const { path, params, fileNameStem, successMessage } = options;
    const { t } = useTranslation();
    const { setSnackbarProps, forceClose } = useSnackbar();

    const mutation = useMutation({
        onMutate: () =>
            setSnackbarProps({
                type: "info",
                message: t("grid.table_actions.exporting", "Exporting…"),
                forceAutoHideInMilisecond: null,
            }),
        mutationFn: async (format: ListExportFormat) => {
            const response = await loggedInApiCall.get<Blob>(`${path}.${format}`, {
                params,
                responseType: "blob",
                headers: { Accept: LIST_EXPORT_MIME_TYPES[format] },
            });
            saveBlob(response.data, listExportFilename(fileNameStem, format));
        },
        onSuccess: () => {
            if (successMessage === undefined) {
                forceClose();
                return;
            }
            setSnackbarProps({ type: "success", message: successMessage });
        },
        onError: (error) => {
            if ((error as AxiosError).response?.status === 401) {
                forceClose();
                return;
            }
            setSnackbarProps({
                type: "error",
                message: t("grid.table_actions.export_error", "The export failed. Try again."),
            });
        },
    });

    const exportList = (format: ListExportFormat) => {
        if (mutation.isLoading) return;
        mutation.mutate(format);
    };

    return { exportList, isExporting: mutation.isLoading };
};
